import type { RenderContext, VideoFrame } from '../../types';
import { BaseEffect } from '../base';
import { effectSeed, withOverlay } from './composite';
import { fbmNoise2D } from '../../utils/noise';
import { canReadCanvasPixels } from '../../canvasSafe';

const DEFAULTS = { density: 0.4, speed: 0.2, height: 0.6 };

export class FogEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../../types').EffectTimelineClip>) {
    super(id, 'fog', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    return this.renderCpu(frame, params, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const seed = effectSeed(context, this.id);
    const t = context.frameIndex * params.speed * 0.02;
    return withOverlay(frame, (ctx, w, h) => {
      const pixelSafe = canReadCanvasPixels(ctx.canvas);
      if (pixelSafe) {
        const img = ctx.getImageData(0, 0, w, h);
        const d = img.data;
        for (let y = 0; y < h; y += 2) {
          const heightFade = Math.pow(1 - y / h, 1 / Math.max(0.2, params.height));
          for (let x = 0; x < w; x += 2) {
            const n = fbmNoise2D(x * 0.004 + t, y * 0.003 + t * 0.5, seed, 3);
            const fog = params.density * heightFade * n * 0.35;
            for (let dy = 0; dy < 2 && y + dy < h; dy += 1) {
              for (let dx = 0; dx < 2 && x + dx < w; dx += 1) {
                const i = ((y + dy) * w + (x + dx)) * 4;
                d[i] = Math.min(255, d[i] + fog * 180);
                d[i + 1] = Math.min(255, d[i + 1] + fog * 190);
                d[i + 2] = Math.min(255, d[i + 2] + fog * 210);
              }
            }
          }
        }
        ctx.putImageData(img, 0, 0);
        return;
      }
      // Tainted canvas fallback — draw fog as translucent layers (no pixel read).
      for (let y = 0; y < h; y += 6) {
        const heightFade = Math.pow(1 - y / h, 1 / Math.max(0.2, params.height));
        for (let x = 0; x < w; x += 6) {
          const n = fbmNoise2D(x * 0.004 + t, y * 0.003 + t * 0.5, seed, 3);
          const fog = params.density * heightFade * n * 0.22;
          if (fog < 0.02) continue;
          ctx.fillStyle = `rgba(210,220,235,${fog})`;
          ctx.fillRect(x, y, 6, 6);
        }
      }
    }, 'source-over');
  }
}
